import { faBook, faClose } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { IPlatformWhatsAppKnowledge } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppKnowledge"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"

type Props = {
  document: IPlatformWhatsAppKnowledge
  onChange: (value: IPlatformWhatsAppKnowledge) => void
  onSave: () => void
  onClose: () => void
}

export const PlatformWhatsAppKnowledgePopup = ({ document, onChange, onSave, onClose }: Props) => {
  const { t } = useTranslation("platformWhatsApp")

  const patch = (value: Partial<IPlatformWhatsAppKnowledge>) => onChange({ ...document, ...value })

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t(document.id === 0 ? "knowledge.createTitle" : "knowledge.editorTitle")}
              icon={faBook}
              iconColor={THEME.colors.iconBGColor}
            >
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

            <div className="grid max-h-[75vh] gap-3 overflow-x-hidden overflow-y-auto p-4 md:grid-cols-2">
              <InputField
                type="text"
                value={document.title}
                placeholder={t("knowledge.title")}
                onChange={(event) => patch({ title: event.target.value })}
                animationDuration="1.1s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.input.title"
              />

              <InputField
                type="text"
                value={document.category}
                placeholder={t("knowledge.category")}
                onChange={(event) => patch({ category: event.target.value })}
                animationDuration="1.2s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.input.category"
              />

              <InputField
                type="text"
                value={document.keywords}
                placeholder={t("knowledge.keywords")}
                onChange={(event) => patch({ keywords: event.target.value })}
                animationDuration="1.3s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.input.keywords"
              />

              <InputField
                type="number"
                value={String(document.priority)}
                placeholder={t("knowledge.priority")}
                onChange={(event) => patch({ priority: Number(event.target.value) || 0 })}
                animationDuration="1.4s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.input.priority"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="md:col-span-2">
                  <SimpleToggle
                    title={t("knowledge.active")}
                    value={document.isActive}
                    onChange={(value) => patch({ isActive: value })}
                    animationDuration="1.2s"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.toggle.active"
                  />
                </div>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                <label className="md:col-span-2 text-sm font-semibold text-slate-700">
                  {t("knowledge.content")}

                  <textarea
                    className="mt-2 w-full rounded-xl border-2 border-slate-200 bg-white p-3 outline-none transition-all duration-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-500"
                    rows={12}
                    maxLength={30000}
                    value={document.content}
                    onChange={(event) => patch({ content: event.target.value })}
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.input.input"
                  />
                </label>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.7s">
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="button"
                    className="rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!document.title.trim() || !document.category.trim() || !document.content.trim()}
                    onClick={onSave}
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgePopup.button.onSave"
                  >
                    {t("actions.save")}
                  </button>
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
